import { ParseTreeWalker } from "antlr4ng";
import { createParser } from "shared/antlr/frendsExpressionFactory";
import { FrendsExpressionParserListener } from "shared/antlr/FrendsExpressionParserListener";
import { EnvRefContext, SourceRefContext } from "shared/antlr/FrendsExpressionParser";

export type VdmExpressionMode = "text" | "csharp";

const SOURCE_KEYWORD = "#source";
const ENV_KEYWORD = "#env";

function sourcePathSuffix(path: string): string {
    if (!path) {
        return "";
    }

    // A path opening with an indexer addresses the source itself (a root array), so it takes no dot.
    return path.startsWith("[") ? path : `.${path}`;
}

function isIdentifierChar(char: string): boolean {
    return /[a-z0-9_åäö]/i.test(char);
}

// First index at or after `index` that is not whitespace.
function skipWhitespace(expression: string, index: number): number {
    while (index < expression.length && /\s/.test(expression[index])) {
        index++;
    }
    return index;
}

function parseSourceRef(expression: string, tokenStart: number): Pick<SourceRef, "path" | "pathEnd" | "tokenEnd"> {
    let index = tokenStart + SOURCE_KEYWORD.length;
    let pathEnd = index;
    let tokenEnd = index;
    const pathParts: string[] = [];

    while (index < expression.length) {
        // A null-conditional indexer (`?[0]`, whitespace allowed around the `?` as in C#) addresses
        // the same schema path as its unconditional form, so the path drops the `?` and downstream
        // consumers (indexer counting, schema lookups) see the plain indexed path. Anything else
        // after a `?` ends the reference: conditional member access (`?.a`) ends the backend's
        // chain too, so segments behind it can neither drive nor follow an iteration.
        let cursor = index;
        const question = skipWhitespace(expression, index);
        if (expression[question] === "?") {
            const continuation = skipWhitespace(expression, question + 1);
            cursor = expression[continuation] === "[" ? continuation : index;
        }
        const currentChar = expression[cursor];

        if (currentChar === "[") {
            const closingBracket = expression.indexOf("]", cursor + 1);
            if (closingBracket === -1) {
                break;
            }

            pathParts.push(expression.substring(cursor, closingBracket + 1));
            index = closingBracket + 1;
            pathEnd = index;
            tokenEnd = index;
            continue;
        }

        if (currentChar !== ".") {
            break;
        }

        let segmentEnd = cursor + 1;
        while (segmentEnd < expression.length && isIdentifierChar(expression[segmentEnd])) {
            segmentEnd++;
        }

        if (segmentEnd === cursor + 1) {
            break;
        }

        tokenEnd = segmentEnd;
        // C# allows whitespace between a method name and its parentheses, e.g. `.Trim ()`.
        if (expression[skipWhitespace(expression, segmentEnd)] === "(") {
            break;
        }

        pathParts.push(expression.substring(cursor, segmentEnd));
        index = segmentEnd;
        pathEnd = segmentEnd;
    }

    return {
        path: pathParts.join("").replace(/^\./, ""),
        pathEnd,
        tokenEnd,
    };
}

export function formatSourceToken(path: string, mode: VdmExpressionMode): string {
    const suffix = sourcePathSuffix(path);
    if (mode === "csharp") {
        return `${SOURCE_KEYWORD}${suffix}`;
    }
    return `{{${SOURCE_KEYWORD}${suffix}}}`;
}

export interface SourceRef {
    path: string;
    start: number;
    // End position (exclusive) of the path portion within the full token
    pathEnd: number;
    // End position (exclusive) of the full ANTLR token (before method-call trimming)
    tokenEnd: number;
}

// Collects SOURCE_REF tokens from a parsed expression, trimming trailing method-call segments.
// E.g. #source.Name.ToString() → path "Name", pathEnd at end of "Name".
export function collectSourceRefs(expression: string, mode: VdmExpressionMode): SourceRef[] {
    const language = mode === "csharp" ? "csharp" : "handlebars";
    const parser = createParser(expression, language);
    const tree = mode === "csharp"
        ? parser.csExpressionRoot()
        : parser.textExpressionRoot();

    const refs: SourceRef[] = [];
    const listener = new FrendsExpressionParserListener();
    listener.enterSourceRef = (ctx: SourceRefContext) => {
        const token = ctx.SOURCE_REF().symbol;
        const tokenStart = token.start;
        const { path, pathEnd, tokenEnd } = parseSourceRef(expression, tokenStart);

        refs.push({ path, start: tokenStart, pathEnd, tokenEnd });
    };
    ParseTreeWalker.DEFAULT.walk(listener, tree);
    return refs;
}

export function extractSourcePaths(expression: string, mode: VdmExpressionMode = "text"): string[] {
    if (!expression || !expression.toLowerCase().includes(SOURCE_KEYWORD)) {
        return [];
    }
    const refs = collectSourceRefs(expression, mode);
    return Array.from(new Set(refs.map((ref) => ref.path)));
}

// Extracts environment variable keys from an expression.
// E.g. "{{#env.Group1.Var1}}" -> ["Group1.Var1"]
export function extractEnvPaths(expression: string, mode: VdmExpressionMode): string[] {
    if (!expression || !expression.toLowerCase().includes(ENV_KEYWORD)) {
        return [];
    }
    const language = mode === "csharp" ? "csharp" : "handlebars";
    const parser = createParser(expression, language);
    const tree = mode === "csharp"
        ? parser.csExpressionRoot()
        : parser.textExpressionRoot();
    const paths = new Set<string>();
    const listener = new FrendsExpressionParserListener();
    listener.enterEnvRef = (ctx: EnvRefContext) => {
        // ENV_REF_START text is like "#env.GroupName", ENV_VAR_VALUE is like ".VarName"
        const refStart = ctx.ENV_REF_START().getText();
        const group = refStart.substring(ENV_KEYWORD.length + 1); // strip "#env."
        const varValue = ctx.ENV_VAR_VALUE();
        paths.add(varValue ? group + varValue.getText() : group); // varValue includes leading dot
    };
    ParseTreeWalker.DEFAULT.walk(listener, tree);
    return Array.from(paths);
}

// Arguments of a method call: anything but parens, with double-quoted strings (which may contain
// parens, `#`, and backslash escapes) treated as opaque, e.g. `.Replace("(#)", "\"")`. A bare `#`
// outside a string is excluded so an argument carrying a real reference (e.g. `Replace(#source.b,
// "-")`) declines rather than risks removing that reference too.
const CALL_ARGS = String.raw`\((?:[^()#"]|"(?:[^"\\]|\\.)*")*\)`;
// A trailing method-call chain on a source ref, e.g. `("-", "")` or `().ToUpper()` — or nothing,
// so a plain `{{#source.a}}` matches too. Anything else (other references, arithmetic) declines.
// The identifier char class mirrors ID_CHAR in FrendsExpressionLexer.g4 (as does isIdentifierChar).
const METHOD_CALL_TAIL = new RegExp(String.raw`^\s*(?:${CALL_ARGS}(?:\s*\.\s*[a-z0-9_åäö]+\s*${CALL_ARGS})*)?\s*$`, "i");
// A method-call chain directly following a source ref, e.g. `.Trim().ToUpper ()` — possibly empty.
// Used in csharp mode, where no `}}` bounds the token, to consume the whole call. Whitespace is
// permitted around the dots too, since C# ignores whitespace between tokens.
const CALL_CHAIN = new RegExp(String.raw`^(?:\s*\.\s*[a-z0-9_åäö]+\s*${CALL_ARGS})*`, "i");

// Whether `open` (an index of `{{`) begins a plain double-brace interpolation whose only content
// before the ref is the ref itself — not a triple-stash `{{{...}}}` (a `{` sits just before, so the
// lexer opened one char earlier) and not a helper/composite like `{{#if #source.a}}`.
function isLoneInterpolationOpen(expression: string, open: number, refStart: number): boolean {
    return open !== -1
        && expression[open - 1] !== "{"
        && expression.substring(open + 2, refStart).trim() === "";
}

// Span of the `{{...}}` interpolation wrapping a source ref, or null when the ref is not the sole
// content of a plain interpolation (e.g. `{{#if #source.a}}`, `{{#source.a + #source.b}}`). Those
// are left untouched rather than risking a mangled expression.
function interpolationSpan(expression: string, ref: SourceRef): { start: number; end: number } | null {
    const open = expression.lastIndexOf("{{", ref.start);
    if (!isLoneInterpolationOpen(expression, open, ref.start)) {
        return null;
    }

    const close = expression.indexOf("}}", ref.tokenEnd);
    if (close === -1 || !METHOD_CALL_TAIL.test(expression.substring(ref.tokenEnd, close))) {
        return null;
    }

    return { start: open, end: close + 2 };
}

// csharp mode: splice out each matching ref and any method-call chain trailing it (there is no `}}`
// to bound the token), back to front so offsets stay valid, then drop any dangling operator.
function removeSourcePathCsharp(expression: string, path: string, refs: SourceRef[]): string {
    let result = expression;
    for (let i = refs.length - 1; i >= 0; i--) {
        if (refs[i].path !== path) {
            continue;
        }
        const chain = CALL_CHAIN.exec(expression.substring(refs[i].pathEnd));
        const end = refs[i].pathEnd + (chain?.[0].length ?? 0);
        // A member access left after the chain (e.g. a call whose argument carries another
        // reference) would end up dangling on nothing - decline like text mode does, leaving the
        // custom expression for the mapping editor.
        if (expression[skipWhitespace(expression, end)] === ".") {
            continue;
        }
        result = result.substring(0, refs[i].start) + result.substring(end);
    }
    return result
        // collapse each operator type left adjacent by a removed middle operand, e.g. "a +  + c" →
        // "a + c". Kept separate per operator so a mixed "a +  && c" is not silently rewritten.
        .replace(/\+(?:\s*\+)+/g, "+")
        .replace(/&&(?:\s*&&)+/g, "&&")
        // drop a dangling operator at either end, e.g. " + #source.b" → "#source.b"
        .replace(/^\s*(\+|&&)\s*/, "")
        .replace(/\s*(\+|&&)\s*$/, "");
}

// text mode: splice out the whole `{{...}}` interpolation wrapping each matching ref, back to front.
// Parsing (rather than matching `{{#source.path}}`) is what lets tokens carrying a method call
// (e.g. `{{#source.orderDate.Replace("-", "")}}`) be removed too.
function removeSourcePathText(expression: string, path: string, refs: SourceRef[]): string {
    let result = expression;
    for (let i = refs.length - 1; i >= 0; i--) {
        const span = refs[i].path === path ? interpolationSpan(expression, refs[i]) : null;
        if (span) {
            result = result.substring(0, span.start) + result.substring(span.end);
        }
    }
    return result;
}

export function removeSourcePath(expression: string, path: string, mode: VdmExpressionMode = "text"): string {
    if (!expression || !expression.toLowerCase().includes(SOURCE_KEYWORD)) {
        return expression;
    }
    const refs = collectSourceRefs(expression, mode);
    return mode === "csharp"
        ? removeSourcePathCsharp(expression, path, refs)
        : removeSourcePathText(expression, path, refs);
}

export function appendSourcePath(expression: string, path: string, mode: VdmExpressionMode = "text", targetType?: string): string {
    const token = formatSourceToken(path, mode);
    if (!expression || expression.trim().length === 0) {
        return token;
    }
    if (mode === "csharp") {
        const operator = targetType === "boolean" ? " && " : " + ";
        return `${expression}${operator}${token}`;
    }
    return `${expression}${token}`;
}

// Matches a whole handlebars block, braces included.
const HANDLEBARS_BLOCK = /\{\{[^}]*\}\}/g;

// A source reference and nothing else: "#source", "#source.a.b", "#source.items[0].sku". Only
// whole-number indexers are allowed, so anything computed inside the brackets counts as code.
// The identifier class mirrors ID_CHAR in FrendsExpressionLexer.g4.
const PLAIN_SOURCE_REFERENCE = /^#source(\.[a-z0-9_åäö]+|\[\d+\])*$/i;

// Checks whether the expression contains a #source reference outside of {{}} handlebars (text mode only).
export function hasBareReference(expression: string): boolean {
    if (!expression) {
        return false;
    }
    const withoutHandlebars = expression.replace(HANDLEBARS_BLOCK, "");
    return /#source\b/i.test(withoutHandlebars);
}

// Whether the expression is a reference rather than a program: in csharp mode one bare reference,
// in text mode static text around handlebars blocks that each hold nothing but a reference. Method
// calls, operators, casts, interpolation and handlebars helpers all fail. A value with no reference
// at all fails too, since a mapping that carries no source data is invented rather than mapped.
export function isPlainReferenceExpression(expression: string, mode: VdmExpressionMode): boolean {
    const trimmed = expression.trim();
    if (mode === "csharp") {
        return PLAIN_SOURCE_REFERENCE.test(trimmed);
    }
    if (hasBareReference(trimmed)) {
        return false;
    }
    const blocks: string[] = trimmed.match(HANDLEBARS_BLOCK) ?? [];
    return blocks.length > 0
        && blocks.every(block => PLAIN_SOURCE_REFERENCE.test(block.slice(2, -2).trim()));
}

// Whether the expression contains handlebars syntax ({{#...}}), invalid in csharp mode - the csharp-side mirror of hasBareReference.
export function hasHandlebarsReference(expression: string): boolean {
    if (!expression) {
        return false;
    }
    return /\{\{\s*#/.test(expression);
}

// Converts a direct text mapping to its csharp form by matching the value against what the direct-mapping
// generator would produce: a lone "{{#source.x}}" / "{{#env.x}}" becomes "#source.x" / "#env.x". Returns
// null for anything else (composite text, other reference types, handlebars helpers), leaving it untouched
// so it stays clearly broken for the user to fix.
export function directTextMappingToCsharp(value: string): string | null {
    const trimmed = value.trim();

    const sourcePaths = extractSourcePaths(trimmed, "text");
    if (sourcePaths.length === 1 && trimmed === formatSourceToken(sourcePaths[0], "text")) {
        return formatSourceToken(sourcePaths[0], "csharp");
    }

    const envPaths = extractEnvPaths(trimmed, "text");
    if (envPaths.length === 1 && trimmed === `{{#env.${envPaths[0]}}}`) {
        return `#env.${envPaths[0]}`;
    }

    return null;
}

// Resolves the editor mode when opening a mapping for edit. Text mode is only valid for a string
// target; for any non-string target the mode must be csharp (the Text/Expression toggle is hidden
// there). String targets keep the stored choice, since both modes are valid.
export function resolveEditorMode(
    initialMode: VdmExpressionMode | undefined,
    targetType: string,
): VdmExpressionMode {
    return targetType === "string" ? (initialMode ?? "text") : "csharp";
}
